import { COPY } from '../copy/vi.ts';

/**
 * §28.3 — the "check-in opens at" pill renders `starts_at` in the event's own
 * timezone, not the device's. A participant in a different timezone (or with
 * a wrong device clock) must still see the organizer's stated time.
 */
export function formatOpensAt(
  startsAt: string | null | undefined,
  timeZone: string,
): string | null {
  if (!startsAt) return null;

  const date = new Date(startsAt);
  if (Number.isNaN(date.getTime())) return null;

  try {
    const parts = new Intl.DateTimeFormat('vi-VN', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour12: false,
    }).formatToParts(date);

    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)?.value ?? '';

    return `${get('hour')}:${get('minute')}, ${get('day')}/${get('month')}/${get('year')}`;
  } catch {
    // An unknown IANA zone must not blank the screen; drop the pill instead.
    return null;
  }
}

/**
 * Split one instant into the wall-clock parts of a given timezone.
 *
 * Returns null for an unparseable instant or an IANA zone the runtime does
 * not know, which is what lets every caller below drop a line instead of
 * printing "Invalid Date" at a participant.
 */
function wallClock(
  iso: string | null | undefined,
  timeZone: string,
): { time: string; date: string } | null {
  if (!iso) return null;

  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return null;

  try {
    const parts = new Intl.DateTimeFormat('vi-VN', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour12: false,
    }).formatToParts(instant);

    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)?.value ?? '';

    return {
      time: `${get('hour')}:${get('minute')}`,
      date: `${get('day')}/${get('month')}/${get('year')}`,
    };
  } catch {
    return null;
  }
}

/**
 * The event's own window, for the info panel below the check-in button.
 *
 * Both ends are resolved in the event's timezone before they are compared, so
 * whether an event "ends the same day" is answered where the event happens
 * rather than where the phone is. A single-day event prints its date once;
 * anything else would make a participant read the same date twice to notice
 * it had not changed.
 *
 * An organizer who published only a closing time still gets a line, labelled,
 * because a stated end with no start is information rather than a mistake.
 */
export function formatEventSchedule(
  startsAt: string | null | undefined,
  endsAt: string | null | undefined,
  timeZone: string,
): string | null {
  const start = wallClock(startsAt, timeZone);
  const end = wallClock(endsAt, timeZone);

  if (start && end) {
    return start.date === end.date
      ? `${start.time} – ${end.time}, ${start.date}`
      : `${start.time} ${start.date} – ${end.time} ${end.date}`;
  }
  if (start) return `${start.time}, ${start.date}`;
  if (end) return `${COPY.eventInfo.scheduleEndPrefix} ${end.time}, ${end.date}`;
  return null;
}

/**
 * `HH:MM:SS DD/MM` in the event's timezone — the admin list (§23).
 *
 * Seconds are included because staff use this to reconcile a specific person
 * with a specific moment at the desk, and two people a minute apart is common
 * at a busy location.
 */
export function formatCheckinTime(iso: string, timeZone: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  try {
    const parts = new Intl.DateTimeFormat('vi-VN', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      day: '2-digit',
      month: '2-digit',
      hour12: false,
    }).formatToParts(date);

    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)?.value ?? '';

    return `${get('hour')}:${get('minute')}:${get('second')} ${get('day')}/${get('month')}`;
  } catch {
    return '';
  }
}

/** Thousands separators, so 126432 reads as 126.432 at a glance (§23). */
export function formatCount(value: number): string {
  return new Intl.NumberFormat('vi-VN').format(value);
}
